import React from 'react';
import s from './CartItem.scss';
import {classes} from './CartItem.st.css';
import {CartItemThumbnail} from '../CartItemThumbnail/CartItemThumbnail';
import {Name} from './partials/Name/Name';
import {Discounts} from './partials/Discounts/Discounts';
import {Price} from './partials/Price/Price';
import {Remove} from './partials/Remove/Remove';
import {Quantity} from './partials/Quantity/Quantity';
import {TotalPrice} from './partials/TotalPrice/TotalPrice';
import {OutOfStock} from './partials/OutOfStock/OutOfStock';
import {PaymentTypeLabel} from './partials/PaymentTypeLabel/PaymentTypeLabel';
import {useControllerProps} from '../../../../domain/controllers/ControllerContext';
import {ViolationNotification} from '../../Violations/ViolationNotification/ViolationNotification';
import {Spinner, SpinnerTypes} from '@wix/wix-ui-tpa';
import {Divider} from '@wix/wix-ui-tpa/cssVars';
import classNames from 'clsx';
import {useTranslation} from '@wix/yoshi-flow-editor';
import {LineItemModel} from '../../../../domain/models/LineItems/LineItem.model';
import {ViolationModel, ViolationSeverity} from '../../../../domain/models/CartSummary/Violation.model';
import {ItemDetails} from './partials/ItemDetails/ItemDetails';
import {MAX_DESCRIPTION_LINE_LENGTH} from '../../../constants';
import {mapLineItemToDetails} from './partials/ItemDetails/mapLineItemToDetails';
import {ItemSubscription} from './partials/ItemSubscription/ItemSubscription';
import {PaymentOption} from '../../../../domain/models/LineItems/PaymentConfig.model';

export enum CartItemDataHook {
  'Root' = 'CartItemDataHook.root',
  'ComparePrice' = 'CartItemDataHook.comparePrice',
  'ComparePriceOnLineItem' = 'CartItemDataHook.comparePriceOnLineItem',
  'Name' = 'CartItemDataHook.name',
  'Options' = 'CartItemDataHook.options',
  'Price' = 'CartItemDataHook.price',
  'PaymentTypeLabel' = 'CartItemDataHook.paymentTypeLabel',
  'Quantity' = 'CartItemDataHook.quantity',
  'QuantityErrorTooltip' = 'CartItemDataHook.quantityErrorTooltip',
  'OutOfStockRoot' = 'CartItemDataHook.outOfStockRoot',
  'OutOfStockError' = 'CartItemDataHook.outOfStockError',
  'Remove' = 'CartItemDataHook.remove',
  'TotalPrice' = 'CartItemDataHook.totalPrice',
  'Discounts' = 'CartItemDataHook.Discounts',
  'DiscountName' = 'CartItemDataHook.DiscountName',
  'Divider' = 'CartItemDataHook.divider',
  'loadingSpinner' = 'CartItemDataHook.loadingSpinner',
  'SubscriptionFrequency' = 'CartItemDataHook.subscriptionFrequency',
  'Subscription' = 'CartItemDataHook.subscription',
  'totalPriceSkeleton' = 'CartItemDataHook.totalPriceSkeleton',
}

interface CartItemProps {
  lineItem: LineItemModel;
  showQuantityBySettings: boolean;
  showThumbnailBySettings: boolean;
  shouldShowItemInfoBySettings: boolean;
  showDivider: boolean;
  onRemoveItem?: () => void;
  quantityClassName?: string;
  thumbnailClassName?: string;
  infoClassName?: string;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export const CartItem: React.FC<CartItemProps> = (props: CartItemProps) => {
  const {
    lineItem,
    showQuantityBySettings,
    showThumbnailBySettings,
    shouldShowItemInfoBySettings,
    showDivider,
    onRemoveItem,
    quantityClassName,
    thumbnailClassName,
  } = props;
  const {
    cartStore: {shouldDisplayViolations, loadingItems, cartSummary, isSummaryUpdating, changeCartLoaders},
  } = useControllerProps();

  const {t} = useTranslation();

  const shouldHideQuantity = lineItem.quantityInfo.fixedQuantity || !showQuantityBySettings;
  const shouldHidePrice = lineItem.pricing.priceUndetermined;
  const shouldShowPaymentTypeLabel =
    lineItem.paymentConfig.paymentOption && lineItem.paymentConfig.paymentOption !== PaymentOption.FULL_PAYMENT_ONLINE;
  const isItemOutOfStock = lineItem.quantityInfo.availableQuantity === 0;

  const getLineItemViolationIfItExists = (): ViolationModel | undefined => {
    if (shouldDisplayViolations) {
      return (
        cartSummary?.violations.find(
          (violation) =>
            violation.target?.lineItemTarget?.id === lineItem.id && violation.severity === ViolationSeverity.error
        ) ??
        cartSummary?.violations.find(
          (violation) =>
            violation.target?.lineItemTarget?.id === lineItem.id && violation.severity === ViolationSeverity.warning
        )
      );
    }
  };

  const lineItemViolation = getLineItemViolationIfItExists();
  const shouldShowLineItemViolation = !isItemOutOfStock && Boolean(lineItemViolation);
  const isItemLoading = isSummaryUpdating && loadingItems.includes(lineItem.id);

  const hasModifiersPrice = lineItem.pricing.breakdown.totalModifiersPrice?.convertedAmount !== 0;
  const priceOnShowMore = hasModifiersPrice
    ? lineItem.pricing.breakdown.totalModifiersPrice?.formattedConvertedAmount
    : undefined;

  return (
    <>
      <div className={s.itemWrapper} data-hook={CartItemDataHook.Root}>
        <div className={classNames(s.item, ...(isItemLoading && !changeCartLoaders ? [s.loading] : []))}>
          <div className={s.info}>
            {isItemLoading && !changeCartLoaders ? (
              <Spinner data-hook={CartItemDataHook.loadingSpinner} isCentered type={SpinnerTypes.slim} />
            ) : undefined}
            {showThumbnailBySettings && <CartItemThumbnail lineItem={lineItem} className={thumbnailClassName} />}
            <div className={s.details}>
              <div className={s.content}>
                <Name lineItem={lineItem} />
                <Discounts lineItem={lineItem}></Discounts>
                {!shouldHidePrice && (
                  <Price lineItem={lineItem} shouldShowCrossedOutPriceOnLineItemLevel={hasModifiersPrice} />
                )}
                {shouldShowPaymentTypeLabel && <PaymentTypeLabel lineItem={lineItem} />}
                {
                  <div className={classes.itemData}>
                    {lineItem.attributes.subscriptionInfo && (
                      <ItemSubscription
                        subscription={lineItem.attributes.subscriptionInfo}
                        dataHook={CartItemDataHook.Subscription}
                      />
                    )}
                    {shouldShowItemInfoBySettings && (
                      <ItemDetails
                        dataHook={CartItemDataHook.Options}
                        itemId={lineItem.id}
                        details={mapLineItemToDetails(lineItem, t)}
                        maxOptionValLength={MAX_DESCRIPTION_LINE_LENGTH}
                        collapseSettings={{
                          numberOfOptionsToCollapseAfter: 1,
                          showMoreTitle: t('cart.lineItems.showMore', {modifierTotal: priceOnShowMore}),
                          showLessTitle: t('cart.lineItems.showLess'),
                        }}
                      />
                    )}
                  </div>
                }
              </div>
              {
                <div className={s.priceAndQuantity}>
                  {!shouldHideQuantity && <Quantity lineItem={lineItem} className={quantityClassName} />}
                  <TotalPrice
                    lineItem={lineItem}
                    shouldShowCrossedOutPriceOnLineItemLevel={hasModifiersPrice}
                    isItemLoading={isItemLoading}
                  />
                </div>
              }
            </div>
          </div>
          <div className={s.colEnd}>
            <Remove lineItem={lineItem} onRemoveItem={onRemoveItem} />
          </div>
        </div>
        {isItemOutOfStock && <OutOfStock />}
        {shouldShowLineItemViolation && (
          <div className={s.violation}>
            <ViolationNotification violation={lineItemViolation} />
          </div>
        )}
      </div>
      {showDivider ? <Divider className={classes.divider} data-hook={CartItemDataHook.Divider} /> : null}
    </>
  );
};
